import Close from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';

interface PricingHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fixed heading, e.g. "Pricing history". */
  title: string;
  /** The object being viewed, e.g. "Order · ORD-1234" — shown next to the title. */
  subtitle: string;
  /** The timeline to render — PricingEventsTimeline (job/order) or ProjectTimeline (project). */
  children: ReactNode;
}

/**
 * Right slide-out panel that hosts a read-only rate/pricing timeline for one object. The shell is
 * object-agnostic — the caller supplies the timeline as children and a subtitle naming the object —
 * so job, order, and project histories share one panel. Mirrors the order form's Drawer pattern.
 */
export default function PricingHistoryPanel({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
}: PricingHistoryPanelProps) {
  const theme = useTheme();

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={isOpen}
      sx={{
        // Sit above the Edit Rates dialog it can be opened from — both default to zIndex.modal.
        zIndex: theme.zIndex.modal + 1,
        [`& .${drawerClasses.paper}`]: {
          height: 'calc(100dvh - var(--tread-agent-dock-height, 0px))',
          // Matches the app's drawer convention (80% width); the Edit/Create Order forms cap at
          // 1200px, this timeline caps a bit narrower with room for the future filtered table.
          maxWidth: '1000px',
          overflow: 'hidden',
          width: '80%',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            alignItems: 'center',
            borderBottom: `solid 1px ${theme.palette.divider}`,
            display: 'flex',
            flex: 0,
            gap: 2,
            justifyContent: 'space-between',
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="h5">
            {title}
            <Typography
              component="span"
              sx={{ color: theme.brandV2.colors.treadGray3, fontSize: '16px', ml: 2 }}
            >
              {subtitle}
            </Typography>
          </Typography>

          <IconButton color="inherit" onClick={onClose} size="small">
            <Close sx={{ height: 16, width: 16 }} />
          </IconButton>
        </Box>

        {/* No top padding: a sticky table header inside must stick flush to the top of the scroll
            area, otherwise rows bleed through the gap above it. Children own their top spacing. */}
        <Box sx={{ flex: 1, overflow: 'auto', pb: 1, pt: 0, px: 2 }}>{children}</Box>
      </Box>
    </Drawer>
  );
}
